import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import MiscRecordsModuleDropdown from './MiscRecordsModuleDropdown'
import { useLocation, useHistory } from 'react-router'
import { Body, FlexWrapper } from '../Shell/Shell'
import { useSelector, useDispatch } from 'react-redux'
import { setMiscRecordsSelectedModule, setErrorToast } from '../../actions'
import { Spinner } from '../Loaders/Loaders'
import { DragProvider, DragState } from '../DragAndDrop/DragContext'
import Droppable from '../DragAndDrop/Droppable'
import Category from '../FormManager/Category'
import FloatingMenu from '../FloatingMenu/FloatingMenu'
import ICONS, { MISC_FORM_MANAGER_ICON_KEYS } from '../Icons/Icons'
import FabButton, { FabSubButton } from '../Shell/FabButton'
import AddCategoryIcon from '@tecuity/sos-icons/react/add-category'
import AddWizardIcon from '@tecuity/sos-icons/react/add-wizard'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import DocumentAddIcon from '@tecuity/sos-icons/react/document-add'
import ImportIcon from '@tecuity/sos-icons/react/import'
import ImportDialog from '../FormManager/ImportDialog'
import { ExpandableFormBlock } from '../Forms/Forms'
import FileDownloader from '../FormManager/FileDownloader'
import {
  GET_MISC_FORMS_MANAGER_PAGE,
  GET_MISC_FORMS_PAGE,
  UPDATE_MISC_FORMS_ICON,
  CREATE_MISC_FORM_CATEGORY,
  RENAME_MISC_FORM_CATEGORY,
  DELETE_MISC_FORM_CATEGORY,
  MOVE_MISC_FORM_CATEGORY,
  PUBLISH_MISC_FORM_CATEGORY,
  UNPUBLISH_MISC_FORM_CATEGORY,
  ARCHIVE_MISC_FORM,
  CLONE_MISC_FORM,
  MOVE_MISC_FORM,
  IMPORT_MISC_FORM,
  CHECK_MISC_FORM_ID_IN_USE,
  LOAD_MISC_WIZARD,
} from '../../requests'
import './MiscellaneousFormsPage.scss'


const getMiscFormsErrorMessage = (err) => {
  // Bare 401 responses from ASP.NET have no body (err === '').
  // JSON ProblemDetails 401 responses include a status field.
  if (!err || err.status === 401) {
    return 'You are not authorized to modify forms for miscellaneous modules.'
  }
  return err.Message || err.ExceptionMessage || 'An unexpected error occurred.'
}

const toWizardShape = (form) => ({
  FORM_DEFINITION_ID: form.FormDefinitionId,
  TITLE: form.Title,
  DESCR: form.Description,
  LAST_MODIFIED_DATE: form.LastModifiedDate,
  LAST_MODIFIED_BY_NAME: form.LastModifiedByName,
})

const MiscellaneousFormsPage = ({ sublinks, isFormsManager = false, ...props }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const miscRecordsSelectedModules = useSelector((state) => state.app.miscRecordsSelectedModules)
  const [formsData, setFormsData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState('')
  const [currentIcon, setCurrentIcon] = useState(null)
  const [iconMenuOpen, setIconMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [fabImportOpen, setFabImportOpen] = useState(false)
  const [fileString, setFileString] = useState('')
  const [fileName, setFileName] = useState('')
  const [downloadNextTick, setDownloadNextTick] = useState(false)
  const iconButtonRef = useRef()
  const fileDownloaderRef = useRef()

  const sourceTypeParam = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location])

  const modules = useMemo(() => {
    const parts = location.pathname.split('/')
    const sublink = sublinks.find((x) => x.parameter === parts[parts.length - 1])
    if (!sublink) return []
    return isFormsManager
      ? sublink.MiscRecordsModules.filter((x) => x.DisplayInFormsManager)
      : sublink.MiscRecordsModules.filter((x) => x.DisplayInForms)
  }, [location, sublinks, isFormsManager])

  const selectedModule = useMemo(() => {
    return miscRecordsSelectedModules[sourceTypeParam]
      ? modules.find(
          (m) => m.MiscellaneousModuleId === +miscRecordsSelectedModules[sourceTypeParam],
        )
      : {}
  }, [miscRecordsSelectedModules, sourceTypeParam, modules])

  useEffect(() => {
    if (!selectedModule.MiscellaneousModuleId && modules.length > 0) {
      dispatch(setMiscRecordsSelectedModule(sourceTypeParam, modules[0].MiscellaneousModuleId))
    }
  }, [selectedModule, modules])

  useEffect(() => {
    if (!selectedModule.MiscellaneousModuleId) return
    setCurrentIcon(selectedModule.FormsIcon || null)
    setFilter('')
    setLoading(true)
    const fetchPage = isFormsManager ? GET_MISC_FORMS_MANAGER_PAGE : GET_MISC_FORMS_PAGE
    fetchPage(
      selectedModule.MiscellaneousModuleId,
      (data) => {
        setFormsData(data)
        setLoading(false)
      },
      (err) => {
        dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
        setLoading(false)
      }
    )
  }, [selectedModule.MiscellaneousModuleId])

  useEffect(() => {
    if (downloadNextTick) {
      fileDownloaderRef.current && fileDownloaderRef.current.download()
      setDownloadNextTick(false)
    }
  }, [downloadNextTick])

  const refetchFormsData = useCallback(() => {
    if (!selectedModule.MiscellaneousModuleId) return
    GET_MISC_FORMS_MANAGER_PAGE(
      selectedModule.MiscellaneousModuleId,
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }, [selectedModule.MiscellaneousModuleId])

  const startNewWizard = () => history.push('/FormsManager/edit/0', { category: sourceTypeParam, miscellaneousModuleId: selectedModule.MiscellaneousModuleId })
  const loadForm = (formDefinitionId) => history.push('/forms/miscellaneous/new/' + formDefinitionId)
  const editMiscWizard = (formDefinitionId) => history.push('/FormsManager/edit/' + formDefinitionId, { category: sourceTypeParam, miscellaneousModuleId: selectedModule.MiscellaneousModuleId })

  // --- Icon picker ---

  const openIconMenu = () => {
    setMenuAnchor(iconButtonRef.current)
    setIconMenuOpen(true)
  }

  const closeIconMenu = () => {
    setIconMenuOpen(false)
  }

  const handleIconSelect = ({ value }) => {
    UPDATE_MISC_FORMS_ICON(
      selectedModule.MiscellaneousModuleId,
      value,
      () => {
        setCurrentIcon(value)
        selectedModule.FormsIcon = value
      },
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
    closeIconMenu()
  }

  // --- Category actions ---

  const handleAddCategory = () => {
    CREATE_MISC_FORM_CATEGORY(
      selectedModule.MiscellaneousModuleId,
      { CategoryName: 'New Category', SortOrder: formsData.Categories.length },
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleRenameCategory = (categoryId, categoryName) => {
    RENAME_MISC_FORM_CATEGORY(
      categoryId,
      { CategoryName: categoryName },
      () => refetchFormsData(),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleDeleteCategory = (categoryId) => {
    DELETE_MISC_FORM_CATEGORY(
      selectedModule.MiscellaneousModuleId,
      categoryId,
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleMoveCategory = (categoryId, moveUp) => {
    MOVE_MISC_FORM_CATEGORY(
      selectedModule.MiscellaneousModuleId,
      categoryId,
      moveUp,
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleVisibilityChange = (categoryId, on) => {
    const request = on ? PUBLISH_MISC_FORM_CATEGORY : UNPUBLISH_MISC_FORM_CATEGORY
    request(
      selectedModule.MiscellaneousModuleId,
      categoryId,
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  // --- Form actions ---

  const handleArchiveForm = (formDefinitionId, categoryId) => {
    ARCHIVE_MISC_FORM(
      selectedModule.MiscellaneousModuleId,
      categoryId === 'uncategorized' ? null : categoryId,
      formDefinitionId,
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleCloneForm = (formDefinitionId, categoryId, values) => {
    CLONE_MISC_FORM(
      {
        MiscellaneousModuleId: selectedModule.MiscellaneousModuleId,
        MiscellaneousFormCategoryId: categoryId === 'uncategorized' ? null : categoryId,
        FormDefinitionId: formDefinitionId,
        Title: values?.title,
      },
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleExportForm = (formDefinitionId) => {
    LOAD_MISC_WIZARD(
      formDefinitionId,
      (response) => {
        const title = response.TITLE || response.Title || String(formDefinitionId)
        setFileName(
          `${formDefinitionId} - ${title.replace(/ of /g, ' ').replace(/ and /g, ' ').replace(/ the /g, ' ')}.json`
        )
        setFileString(JSON.stringify(response))
        setDownloadNextTick(true)
      },
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }

  const handleMoveForm = useCallback(({ origin, target }) => {
    const targetCategoryId = target.categoryId === 'uncategorized' ? null : target.categoryId
    const targetForms = targetCategoryId !== null
      ? (formsData.Categories.find((c) => c.MiscellaneousFormCategoryId === targetCategoryId)?.Forms || [])
      : formsData.UncategorizedForms
    const newIndex = target.formDefinitionId
      ? targetForms.findIndex((f) => f.FormDefinitionId === target.formDefinitionId)
      : targetForms.length
    MOVE_MISC_FORM(
      {
        FormDefinitionId: origin.formDefinitionId,
        NewIndex: Math.max(0, newIndex),
        CategoryId: targetCategoryId,
        ModuleId: selectedModule.MiscellaneousModuleId,
      },
      (data) => setFormsData(data),
      (err) => dispatch(setErrorToast(getMiscFormsErrorMessage(err)))
    )
  }, [formsData, selectedModule.MiscellaneousModuleId])

  const handleImport = useCallback((json, preserveId, categoryId, onSuccess, onError) => {
    const resolvedCategoryId = !categoryId || categoryId === 'uncategorized' ? null : categoryId

    const doImport = () => {
      IMPORT_MISC_FORM(
        preserveId,
        selectedModule.MiscellaneousModuleId,
        resolvedCategoryId,
        json,
        (data) => {
          setFormsData(data)
          onSuccess()
        },
        (err) => onError(getMiscFormsErrorMessage(err))
      )
    }

    if (preserveId) {
      const formDefId = json.FORM_DEFINITION_ID || json.FormDefinitionId
      CHECK_MISC_FORM_ID_IN_USE(
        formDefId,
        (inUse) => {
          if (inUse) {
            onError('FormDefinitionId already exists, form has not been imported.')
          } else {
            doImport()
          }
        },
        (err) => onError(getMiscFormsErrorMessage(err))
      )
    } else {
      doImport()
    }
  }, [selectedModule.MiscellaneousModuleId])

  const title = `${selectedModule.DisplayName || 'Miscellaneous Records'} Forms${isFormsManager ? ' Manager' : ''}`

  return (
    <Body className={isFormsManager ? 'form-manager forms' : 'forms'}>
      <div className={isFormsManager ? 'misc-forms-manager-toolbar' : 'toolbar'}>
        <h1>{title}</h1>
        <div style={{ paddingLeft: '5px' }}>
          <MiscRecordsModuleDropdown
            modules={modules}
            sourceTypeParam={sourceTypeParam}
            showLabel={false}
          />
        </div>
      </div>
      {loading && <div className="misc-forms-page-spinner"><Spinner themeColor="neutral" /></div>}
      {!loading && formsData && (
        <>
          <div className="toolbar">
            {isFormsManager && (
              <div className="icon-wrapper">
                <div className="selected-icon" ref={iconButtonRef} onClick={openIconMenu}>
                  {ICONS[currentIcon]}
                </div>
                <FloatingMenu
                  className="fm-icon-menu"
                  isOpen={iconMenuOpen}
                  alignTo={menuAnchor}
                  align="right"
                  onRequestClose={closeIconMenu}
                  options={MISC_FORM_MANAGER_ICON_KEYS.map((key) => ({
                    value: key,
                    label: ICONS[key],
                    onClick: handleIconSelect,
                  }))}
                />
              </div>
            )}
            <div className="filter-wrapper">
              <input
                className="filter"
                type="text"
                placeholder="Filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                aria-label="Filter Forms"
              />
              {filter && (
                <button aria-label="clear filter" className="clear" onClick={() => setFilter('')}>
                  <CrossIcon />
                </button>
              )}
            </div>
          </div>
          {isFormsManager ? (
            <DragProvider>
              <FlexWrapper direction="column">
                {(formsData?.Categories || []).map((category, i) => (
                  <Category
                    key={category.MiscellaneousFormCategoryId}
                    id={category.MiscellaneousFormCategoryId}
                    title={category.CategoryName}
                    icon={currentIcon}
                    visible={category.IsPublic}
                    wizards={category.Forms.map(toWizardShape)}
                    filter={filter}
                    canReorderUp={i !== 0}
                    canReorderDown={i !== formsData.Categories.length - 1}
                    onVisibilityChange={(on) => handleVisibilityChange(category.MiscellaneousFormCategoryId, on)}
                    onTitleChange={(t) => handleRenameCategory(category.MiscellaneousFormCategoryId, t)}
                    onReorderUp={() => handleMoveCategory(category.MiscellaneousFormCategoryId, true)}
                    onReorderDown={() => handleMoveCategory(category.MiscellaneousFormCategoryId, false)}
                    onRequestDelete={() => handleDeleteCategory(category.MiscellaneousFormCategoryId)}
                    onRequestRefetch={refetchFormsData}
                    onWizardEdit={editMiscWizard}
                    onWizardArchive={handleArchiveForm}
                    onWizardClone={handleCloneForm}
                    onWizardExport={handleExportForm}
                    onWizardMoved={handleMoveForm}
                    onWizardImport={handleImport}
                  />
                ))}
              </FlexWrapper>
              <Droppable
                accepts="WIZARD"
                onDrop={(data) => handleMoveForm({ origin: data, target: { categoryId: 'uncategorized' } })}
                className="uncategorized-wrapper"
              >
                <DragState>
                  {({ isDragging }) =>
                    isDragging && (
                      <div className="unpublish-shade animated fadeIn fast-animation">
                        Unpublish Form
                      </div>
                    )
                  }
                </DragState>
                <Category
                  id="uncategorized"
                  title="Unpublished Forms"
                  icon={currentIcon}
                  uncategorized
                  wizards={formsData.UncategorizedForms.map(toWizardShape)}
                  filter=""
                  canReorderUp={false}
                  canReorderDown={false}
                  onWizardEdit={editMiscWizard}
                  onWizardArchive={handleArchiveForm}
                  onWizardClone={handleCloneForm}
                  onWizardExport={handleExportForm}
                  onWizardMoved={handleMoveForm}
                />
              </Droppable>
              <FabButton ariaLabel="Expand available actions">
                <FabSubButton tooltip="Create New Category" onClick={handleAddCategory}>
                  <AddCategoryIcon />
                </FabSubButton>
                <FabSubButton tooltip="Create New Form" onClick={startNewWizard}>
                  <AddWizardIcon />
                </FabSubButton>
                <FabSubButton tooltip="Import Form" onClick={() => setFabImportOpen(true)}>
                  <ImportIcon />
                </FabSubButton>
              </FabButton>
            </DragProvider>
          ) : (
            <FlexWrapper direction="column">
              {formsData.Categories.filter((c) => c.IsPublic).map((category) => {
                const filteredForms = filter
                  ? category.Forms.filter((f) => f.Title.toLowerCase().includes(filter.toLowerCase()))
                  : category.Forms
                if (filteredForms.length === 0) return null
                return (
                  <div className="category" key={category.MiscellaneousFormCategoryId}>
                    <div className="title-wrapper">
                      <h2 className="title">{category.CategoryName}</h2>
                    </div>
                    <div className="form-blocks">
                      {filteredForms.map((form) => (
                        <ExpandableFormBlock
                          key={form.FormDefinitionId}
                          label={form.Title}
                          description={form.Description}
                          formDefinitionId={form.FormDefinitionId}
                          onPrimaryButtonClick={loadForm}
                          icon={currentIcon}
                          actionIcon={<DocumentAddIcon />}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
            </FlexWrapper>
          )}
        </>
      )}
      <FileDownloader
        fileString={fileString}
        fileName={fileName}
        contentType="text/json"
        ref={fileDownloaderRef}
      />
      {isFormsManager && (
        <ImportDialog
          isOpened={fabImportOpen}
          closeModal={() => setFabImportOpen(false)}
          closeAndReload={() => setFabImportOpen(false)}
          onImport={handleImport}
        />
      )}
    </Body>
  )
}

export default MiscellaneousFormsPage
